import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';

/*
 * Replaces the Bootstrap modal the AngularJS page opened with jQuery
 * ($('#dataGridModal').modal('show')). Closes on Escape and on a backdrop
 * click, restores focus to whatever opened it, and traps Tab inside the
 * dialog while it is open.
 */
export default function Modal({ title, onClose, children, wide }) {
	const dialogRef = useRef(null);
	const openerRef = useRef(null);

	useEffect(() => {
		openerRef.current = document.activeElement;
		dialogRef.current?.focus();

		const onKeyDown = (event) => {
			if (event.key === 'Escape') {
				onClose();
				return;
			}
			if (event.key !== 'Tab') return;
			const focusable = dialogRef.current?.querySelectorAll(
				'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
			);
			if (!focusable?.length) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener('keydown', onKeyDown);
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		return () => {
			document.removeEventListener('keydown', onKeyDown);
			document.body.style.overflow = previousOverflow;
			openerRef.current?.focus?.();
		};
	}, [onClose]);

	return createPortal(
		<div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
			<div
				className={`modal${wide ? ' modal--wide' : ''}`}
				role="dialog"
				aria-modal="true"
				aria-label={title}
				tabIndex={-1}
				ref={dialogRef}
			>
				<div className="modal__header">
					<h2 className="modal__title">{title}</h2>
					<button
						className="modal__close"
						type="button"
						onClick={onClose}
						aria-label="Close"
					>
						×
					</button>
				</div>
				<div className="modal__body">{children}</div>
			</div>
		</div>,
		document.body,
	);
}
